<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LeadCaptureAPI from 'dashboard/api/leadCaptureEndpoints';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LeadCaptureForm from './LeadCaptureForm.vue';
import LeadCaptureSnippet from './LeadCaptureSnippet.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();
const endpoints = ref([]);
const loading = ref(false);
const showForm = ref(false);
const editing = ref(null);
const snippetFor = ref(null);

// Phase C-4: aba de leads recebidos
const tab = ref('endpoints');
const leads = ref([]);
const loadingLeads = ref(false);
const leadsLoadState = ref('idle');
let leadsRequestId = 0;
// Paginação e filtros no servidor: antes vinham só os 100 mais recentes da
// conta, misturando todos os capturadores.
const leadsPage = ref(1);
const leadsMeta = ref({ total_count: 0, total_pages: 1 });
const leadsFilter = ref({ endpointId: '', days: '' });
const LEAD_PERIODS = [7, 30, 90];

function invalidateLeadsContext() {
  leadsRequestId += 1;
  loadingLeads.value = false;
  leadsLoadState.value = 'idle';
  leads.value = [];
}

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

async function loadLeads() {
  leadsRequestId += 1;
  const requestId = leadsRequestId;
  loadingLeads.value = true;
  leadsLoadState.value = 'loading';
  try {
    const { data } = await LeadCaptureAPI.getLeads({
      page: leadsPage.value,
      endpointId: leadsFilter.value.endpointId,
      days: leadsFilter.value.days,
    });
    if (requestId !== leadsRequestId || tab.value !== 'leads') return;
    leads.value = Array.isArray(data?.leads) ? data.leads : [];
    leadsMeta.value = {
      total_count: data?.meta?.total_count ?? leads.value.length,
      total_pages: data?.meta?.total_pages || 1,
    };
    leadsLoadState.value = 'ready';
  } catch {
    if (requestId !== leadsRequestId || tab.value !== 'leads') return;
    leads.value = [];
    leadsLoadState.value = 'error';
  } finally {
    if (requestId === leadsRequestId) loadingLeads.value = false;
  }
}

function applyLeadsFilter() {
  leadsPage.value = 1;
  loadLeads();
}

function goToLeadsPage(page) {
  if (page < 1 || page > leadsMeta.value.total_pages) return;
  leadsPage.value = page;
  loadLeads();
}

function switchTab(name) {
  tab.value = name;
  if (name !== 'leads') {
    leadsRequestId += 1;
    loadingLeads.value = false;
    if (leadsLoadState.value === 'loading') leadsLoadState.value = 'idle';
    return;
  }
  if (['idle', 'error'].includes(leadsLoadState.value)) loadLeads();
}

watch(
  () => route.params.accountId,
  (accountId, previousAccountId) => {
    if (accountId !== previousAccountId) {
      invalidateLeadsContext();
      if (tab.value === 'leads') loadLeads();
    }
  }
);

onUnmounted(() => {
  leadsRequestId += 1;
});

// Termometro: qualificacao (Phase A) tem prioridade; senao o perfil do lead score.
function qualifBadge(lead) {
  if (lead.qualified === true)
    return {
      label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.QUALIFICATION.QUALIFIED'),
      cls: 'bg-n-teal-3 text-n-teal-11',
    };
  if (lead.qualified === false)
    return {
      label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.QUALIFICATION.DISQUALIFIED'),
      cls: 'bg-n-ruby-3 text-n-ruby-11',
    };
  const p = (lead.profile || '').toLowerCase();
  if (p === 'hot')
    return {
      label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.QUALIFICATION.HOT'),
      cls: 'bg-n-ruby-3 text-n-ruby-11',
    };
  if (p === 'warm')
    return {
      label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.QUALIFICATION.WARM'),
      cls: 'bg-n-amber-3 text-n-amber-11',
    };
  if (p === 'cold')
    return {
      label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.QUALIFICATION.COLD'),
      cls: 'bg-n-blue-3 text-n-blue-11',
    };
  return { label: '-', cls: 'bg-n-slate-3 text-n-slate-11' };
}

function answersPreview(answers) {
  return Object.entries(answers || {})
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
    .join(' · ');
}

function humanDate(d) {
  return d ? new Date(d).toLocaleString('pt-BR') : '-';
}

async function load() {
  loading.value = true;
  try {
    const { data } = await LeadCaptureAPI.get();
    endpoints.value = Array.isArray(data) ? data : [];
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

function startCreate() {
  editing.value = null;
  showForm.value = true;
}
function startEdit(ep) {
  editing.value = ep;
  showForm.value = true;
}

// Excluir e gerar link novo quebram links já publicados: confirmação pelo
// Dialog do design system (antes era window.confirm).
const confirmDialogRef = ref(null);
const pendingAction = ref(null); // { kind: 'delete' | 'rotate', endpoint }
const confirming = ref(false);

const confirmCopy = computed(() => {
  const action = pendingAction.value;
  const name = action?.endpoint?.name || '';
  if (action?.kind === 'rotate') {
    return {
      title: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.CONFIRM.ROTATE_TITLE', {
        name,
      }),
      description: t(
        'INTEGRATION_SETTINGS.LEAD_CAPTURE.CONFIRM.ROTATE_DESCRIPTION'
      ),
      confirm: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.CONFIRM.ROTATE_CONFIRM'),
    };
  }
  return {
    title: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.CONFIRM.DELETE_TITLE', {
      name,
    }),
    description: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.CONFIRM.DELETE_DESCRIPTION'),
    confirm: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.CONFIRM.DELETE_CONFIRM'),
  };
});

function askConfirm(kind, endpoint) {
  pendingAction.value = { kind, endpoint };
  confirmDialogRef.value?.open();
}

function destroy(ep) {
  askConfirm('delete', ep);
}

function rotate(ep) {
  askConfirm('rotate', ep);
}

async function runConfirmedAction() {
  const action = pendingAction.value;
  if (!action || confirming.value) return;
  confirming.value = true;
  const isRotate = action.kind === 'rotate';
  try {
    if (isRotate) await LeadCaptureAPI.rotateSlug(action.endpoint.id);
    else await LeadCaptureAPI.delete(action.endpoint.id);
    useAlert(
      isRotate
        ? t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.ROTATED')
        : t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.DELETED')
    );
    confirmDialogRef.value?.close();
    await load();
  } catch (e) {
    useAlert(
      isRotate
        ? t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.ROTATE_ERROR')
        : t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.DELETE_ERROR')
    );
  } finally {
    confirming.value = false;
  }
}

async function toggleActive(ep) {
  try {
    await LeadCaptureAPI.update(ep.id, { active: !ep.active });
    await load();
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.TOGGLE_ERROR'));
  }
}

function copyText(text) {
  navigator.clipboard
    .writeText(text)
    .then(() => useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.COPIED')))
    .catch(() =>
      useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.COPY_ERROR'))
    );
}

// Formulário Meta aparece aqui só para leitura: o editor desta tela não
// conhece a configuração dele (mapeamento de campos, IA). Editar é em
// Formulários Meta, que é o dono dessa configuração.
const isMetaForm = ep => Boolean(ep?.capture_config?.meta_page_id);
const metaFormsRoute = computed(() => ({
  name: 'settings_integrations_meta_lead_forms',
  params: { accountId: route.params.accountId },
}));

function onFormSaved() {
  showForm.value = false;
  editing.value = null;
  load();
}

const labelByType = {
  wa_redirect: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TYPES.WA_REDIRECT'),
  webhook: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TYPES.WEBHOOK'),
};
// Botões só com ícone da linha: cada um com rótulo para leitor de tela.
const rowActions = ep => [
  {
    key: 'snippet',
    icon: 'i-ri-code-box-line',
    label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.VIEW_SNIPPET'),
    run: () => {
      snippetFor.value = ep;
    },
  },
  {
    key: 'edit',
    icon: 'i-ri-pencil-line',
    label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.EDIT'),
    run: () => startEdit(ep),
  },
  {
    key: 'toggle',
    icon: 'i-ri-toggle-line',
    label: ep.active
      ? t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.DEACTIVATE')
      : t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.ACTIVATE'),
    run: () => toggleActive(ep),
  },
  {
    key: 'rotate',
    icon: 'i-ri-refresh-line',
    label: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.ROTATE'),
    run: () => rotate(ep),
  },
];

const typeLabel = ep =>
  isMetaForm(ep)
    ? t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TYPES.META')
    : labelByType[ep.endpoint_type] || ep.endpoint_type;

const tabs = computed(() => [
  {
    k: 'endpoints',
    l: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABS.ENDPOINTS'),
  },
  { k: 'leads', l: t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABS.LEADS') },
]);

onMounted(() => {
  store.dispatch('inboxes/get');
  load();
});
</script>

<template>
  <div class="p-6 max-w-screen-xl mx-auto">
    <header class="flex justify-between items-start gap-6 mb-6">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12 mb-1">
          {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 max-w-2xl">
          {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.DESCRIPTION') }}
        </p>
      </div>
      <button
        v-if="tab === 'endpoints'"
        class="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-n-brand text-white hover:bg-n-brand/90 transition shrink-0"
        @click="startCreate"
      >
        {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.NEW') }}
      </button>
    </header>

    <!-- Phase C-4: abas Capturadores | Leads recebidos -->
    <div class="flex gap-1 mb-5 border-b border-n-slate-4">
      <button
        v-for="t in tabs"
        :key="t.k"
        class="px-4 py-2 text-sm font-medium -mb-px border-b-2 transition"
        :class="
          tab === t.k
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        @click="switchTab(t.k)"
      >
        {{ t.l }}
      </button>
    </div>

    <template v-if="tab === 'endpoints'">
      <div v-if="loading" class="text-n-slate-11 text-sm">
        {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LOADING') }}
      </div>

      <div
        v-else-if="endpoints.length === 0"
        class="p-12 text-center bg-n-slate-2 rounded-xl text-n-slate-11"
      >
        {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.EMPTY') }}
      </div>

      <div v-else class="overflow-x-auto rounded-xl border border-n-slate-4">
        <table class="w-full text-sm">
          <thead class="bg-n-slate-2 text-n-slate-11">
            <tr>
              <th class="text-left px-4 py-3 font-semibold">
                {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABLE.NAME') }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABLE.TYPE') }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABLE.INBOX') }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABLE.URL') }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABLE.HITS') }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.TABLE.LAST') }}
              </th>
              <th />
            </tr>
          </thead>
          <tbody class="text-n-slate-12">
            <tr
              v-for="ep in endpoints"
              :key="ep.id"
              class="border-t border-n-slate-4"
              :class="[!ep.active && 'opacity-50']"
            >
              <td class="px-4 py-3 align-top">
                <span class="font-semibold">{{ ep.name }}</span>
                <span
                  v-if="!ep.active"
                  class="ml-2 px-2 py-0.5 rounded text-xs bg-n-slate-3"
                  >{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.INACTIVE') }}</span
                >
              </td>
              <td class="px-4 py-3 align-top">
                {{ typeLabel(ep) }}
              </td>
              <td class="px-4 py-3 align-top">{{ ep.inbox_name }}</td>
              <td class="px-4 py-3 align-top min-w-[280px] max-w-[420px]">
                <div class="flex items-center gap-2">
                  <code
                    class="flex-1 text-xs truncate bg-n-slate-2 px-2 py-1.5 rounded font-mono"
                    :title="ep.public_url"
                    >{{ ep.public_url }}</code
                  >
                  <button
                    type="button"
                    class="shrink-0 inline-flex items-center justify-center size-11 rounded border border-n-slate-4 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :title="
                      $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.COPY_URL')
                    "
                    :aria-label="
                      $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.COPY_URL')
                    "
                    @click="copyText(ep.public_url)"
                  >
                    <span class="i-ri-file-copy-line w-5 h-5" />
                  </button>
                </div>
              </td>
              <td class="px-4 py-3 align-top">{{ ep.hits_count }}</td>
              <td class="px-4 py-3 align-top whitespace-nowrap">
                {{
                  ep.last_hit_at
                    ? new Date(ep.last_hit_at).toLocaleString('pt-BR')
                    : '-'
                }}
              </td>
              <td class="px-4 py-3 align-top">
                <router-link
                  v-if="isMetaForm(ep)"
                  :to="metaFormsRoute"
                  class="inline-flex items-center gap-1 min-h-11 px-3 rounded border border-n-slate-4 text-sm text-n-slate-12 hover:bg-n-slate-3 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :title="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.META_READ_ONLY')"
                  :data-testid="`meta-form-link-${ep.id}`"
                >
                  <span class="i-ri-external-link-line size-4" />
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.EDIT_IN_META_FORMS'
                    )
                  }}
                </router-link>
                <div v-else class="flex items-center gap-1">
                  <button
                    v-for="action in rowActions(ep)"
                    :key="action.key"
                    type="button"
                    class="inline-flex items-center justify-center size-11 rounded border border-n-slate-4 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :title="action.label"
                    :aria-label="action.label"
                    :data-testid="`endpoint-${action.key}-${ep.id}`"
                    @click="action.run()"
                  >
                    <span class="size-5" :class="action.icon" />
                  </button>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center size-11 rounded border border-n-ruby-6 text-n-ruby-11 hover:bg-n-ruby-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :title="
                      $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.DELETE')
                    "
                    :aria-label="
                      $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ACTIONS.DELETE')
                    "
                    :data-testid="`endpoint-delete-${ep.id}`"
                    @click="destroy(ep)"
                  >
                    <span class="i-ri-delete-bin-line size-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Phase C-4: tabela de leads recebidos + termometro de qualificacao -->
    <div v-else-if="tab === 'leads'">
      <div
        class="flex flex-wrap items-center gap-2 mb-4"
        data-testid="leads-filters"
      >
        <select
          v-model="leadsFilter.endpointId"
          class="min-h-11 px-3 rounded-xl border border-n-slate-4 bg-n-background text-sm text-n-slate-12"
          :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.FILTER_ENDPOINT')"
          data-testid="leads-filter-endpoint"
          @change="applyLeadsFilter"
        >
          <option value="">
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.ALL_ENDPOINTS') }}
          </option>
          <option v-for="ep in endpoints" :key="ep.id" :value="String(ep.id)">
            {{ ep.name }}
          </option>
        </select>
        <select
          v-model="leadsFilter.days"
          class="min-h-11 px-3 rounded-xl border border-n-slate-4 bg-n-background text-sm text-n-slate-12"
          :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.FILTER_PERIOD')"
          data-testid="leads-filter-period"
          @change="applyLeadsFilter"
        >
          <option value="">
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.ALL_TIME') }}
          </option>
          <option v-for="days in LEAD_PERIODS" :key="days" :value="String(days)">
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.LAST_DAYS', { days }) }}
          </option>
        </select>
        <span
          v-if="leadsLoadState === 'ready'"
          class="text-sm text-n-slate-11"
          data-testid="leads-total"
        >
          {{
            $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.TOTAL', {
              count: leadsMeta.total_count,
            })
          }}
        </span>
      </div>
      <div v-if="loadingLeads" class="text-n-slate-11 text-sm" role="status">
        {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.LOADING') }}
      </div>
      <div
        v-else-if="leadsLoadState === 'error'"
        class="p-8 text-center bg-n-ruby-2 rounded-xl text-n-ruby-11"
        role="alert"
      >
        <p>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.ERROR') }}</p>
        <button
          type="button"
          class="mt-4 inline-flex min-h-11 items-center justify-center rounded-xl border border-n-ruby-7 px-4 text-sm font-medium hover:bg-n-ruby-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
          @click="loadLeads"
        >
          {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.RETRY') }}
        </button>
      </div>
      <div
        v-else-if="leadsLoadState === 'ready' && leads.length === 0"
        class="p-12 text-center bg-n-slate-2 rounded-xl text-n-slate-11"
        role="status"
      >
        {{
          leadsFilter.endpointId || leadsFilter.days
            ? $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.EMPTY_FILTERED')
            : $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.EMPTY')
        }}
      </div>
      <div v-else class="overflow-x-auto rounded-xl border border-n-slate-4">
        <table class="w-full text-sm">
          <thead class="bg-n-slate-2 text-n-slate-11">
            <tr>
              <th class="text-left px-4 py-3 font-semibold">
                {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.TABLE.LEAD') }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.TABLE.QUALIFICATION'
                  )
                }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{
                  $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.TABLE.ANSWERS')
                }}
              </th>
              <th class="text-left px-4 py-3 font-semibold">
                {{
                  $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.TABLE.RECEIVED')
                }}
              </th>
            </tr>
          </thead>
          <tbody class="text-n-slate-12">
            <tr
              v-for="lead in leads"
              :key="lead.id"
              class="border-t border-n-slate-4 align-top"
            >
              <td class="px-4 py-3">
                <a
                  :href="`/app/accounts/${$route.params.accountId}/contacts/${lead.id}`"
                  class="font-semibold text-n-brand hover:underline"
                  >{{
                    lead.name ||
                    $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.TABLE.LEAD')
                  }}</a
                >
                <div class="text-xs text-n-slate-11 mt-0.5">
                  {{ lead.phone }}
                </div>
                <div v-if="lead.email" class="text-xs text-n-slate-11">
                  {{ lead.email }}
                </div>
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex px-2 py-0.5 rounded text-xs font-medium"
                  :class="qualifBadge(lead).cls"
                  >{{ qualifBadge(lead).label }}</span
                >
                <div
                  v-if="lead.score != null"
                  class="text-xs text-n-slate-11 mt-1"
                >
                  {{
                    $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.SCORE', {
                      score: lead.score,
                    })
                  }}
                </div>
              </td>
              <td class="px-4 py-3 max-w-[460px]">
                <span class="text-xs text-n-slate-11 break-words">{{
                  answersPreview(lead.answers) || '-'
                }}</span>
                <div
                  v-if="lead.labels && lead.labels.length"
                  class="flex flex-wrap gap-1 mt-1"
                >
                  <span
                    v-for="l in lead.labels"
                    :key="l"
                    class="px-1.5 py-0.5 rounded text-[10px] bg-n-slate-3 text-n-slate-11"
                    >{{ l }}</span
                  >
                </div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-xs text-n-slate-11">
                {{ humanDate(lead.created_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="leadsLoadState === 'ready' && leadsMeta.total_pages > 1"
        class="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm text-n-slate-11"
        data-testid="leads-pagination"
      >
        <span>
          {{
            $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.PAGE', {
              page: leadsPage,
              pages: leadsMeta.total_pages,
            })
          }}
        </span>
        <div class="flex gap-2">
          <button
            type="button"
            class="min-h-11 px-4 rounded-xl border border-n-slate-4 text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :disabled="leadsPage <= 1 || loadingLeads"
            data-testid="leads-prev"
            @click="goToLeadsPage(leadsPage - 1)"
          >
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.PREVIOUS') }}
          </button>
          <button
            type="button"
            class="min-h-11 px-4 rounded-xl border border-n-slate-4 text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :disabled="leadsPage >= leadsMeta.total_pages || loadingLeads"
            data-testid="leads-next"
            @click="goToLeadsPage(leadsPage + 1)"
          >
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.LEADS.NEXT') }}
          </button>
        </div>
      </div>
    </div>

    <LeadCaptureForm
      v-if="showForm"
      :endpoint="editing"
      :inboxes="inboxes"
      @saved="onFormSaved"
      @close="showForm = false"
    />
    <LeadCaptureSnippet
      v-if="snippetFor"
      :endpoint="snippetFor"
      @close="snippetFor = null"
    />
    <Dialog
      ref="confirmDialogRef"
      type="alert"
      data-testid="lead-capture-confirm"
      :title="confirmCopy.title"
      :description="confirmCopy.description"
      :confirm-button-label="confirmCopy.confirm"
      :is-loading="confirming"
      @confirm="runConfirmedAction"
      @close="pendingAction = null"
    />
  </div>
</template>
